/**
 * UsageDashboard
 * Admin-only token utilization dashboard.
 * Rendered as a tab inside the Admin panel.
 */
import React, { useEffect, useState, useCallback } from 'react'
import {
  AreaChart, Area,
  PieChart, Pie, Cell, Tooltip as ReTooltip, Legend,
  XAxis, YAxis, CartesianGrid, ResponsiveContainer,
} from 'recharts'
import { Download, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { apiFetch } from '../lib/api'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Summary {
  since: string
  until: string
  call_count: number
  active_users: number
  active_projects: number
  trend_pct: number | null
  input_tokens: number
  output_tokens: number
  total_tokens: number
  estimated_cost_usd: number
}

interface DayEntry {
  date: string
  total_tokens: number
  estimated_cost_usd: number
  agent?: { total_tokens: number; estimated_cost_usd: number }
  summarize?: { total_tokens: number; estimated_cost_usd: number }
  extract_api_profile?: { total_tokens: number; estimated_cost_usd: number }
}

interface UserRow {
  user_id: string
  email: string
  call_count: number
  conversation_count: number
  last_active: string
  total_tokens: number
  estimated_cost_usd: number
}

interface ProjectRow {
  project_id: string
  project_name: string
  call_count: number
  conversation_count: number
  last_active: string
  total_tokens: number
  estimated_cost_usd: number
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CALL_TYPE_COLORS: Record<string, string> = {
  agent:               '#10b981',   // emerald
  summarize:           '#3b82f6',   // blue
  extract_api_profile: '#f59e0b',   // amber
}

const CALL_TYPE_LABELS: Record<string, string> = {
  agent:               'Agent',
  summarize:           'Summarize',
  extract_api_profile: 'Extract Profile',
}

const PRESET_RANGES = [
  { label: 'This month',  getSince: () => monthStart(),        getUntil: () => nowIso() },
  { label: 'Last month',  getSince: () => lastMonthStart(),    getUntil: () => monthStart() },
  { label: 'Last 7 days', getSince: () => daysAgo(7),          getUntil: () => nowIso() },
  { label: 'Last 30 days',getSince: () => daysAgo(30),         getUntil: () => nowIso() },
  { label: 'All time',    getSince: () => allTimeStart(),      getUntil: () => nowIso() },
]

// ─── Date helpers ─────────────────────────────────────────────────────────────

function nowIso() {
  return new Date().toISOString()
}
function monthStart() {
  const d = new Date()
  d.setUTCDate(1); d.setUTCHours(0,0,0,0)
  return d.toISOString()
}
function lastMonthStart() {
  const d = new Date()
  d.setUTCMonth(d.getUTCMonth() - 1); d.setUTCDate(1); d.setUTCHours(0,0,0,0)
  return d.toISOString()
}
function daysAgo(n: number) {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - n); d.setUTCHours(0,0,0,0)
  return d.toISOString()
}
function allTimeStart() {
  return '1970-01-01T00:00:00.000Z'
}
function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
function fmtTokens(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}
function fmtCost(n: number) {
  return `$${n.toFixed(2)}`
}
function shortDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({
  label, value, sub, trend,
}: {
  label: string
  value: string
  sub?: string
  trend?: number | null
}) {
  const TrendIcon = trend == null ? Minus : trend > 0 ? TrendingUp : TrendingDown
  const trendColor = trend == null ? '#6b7280' : trend > 0 ? '#10b981' : '#ef4444'

  return (
    <div style={{
      background: 'var(--bg-raised)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: '18px 22px',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      minWidth: 160,
      flex: 1,
    }}>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </span>
      <span style={{ fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>
        {value}
      </span>
      {(sub || trend !== undefined) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)' }}>
          {sub && <span>{sub}</span>}
          {trend !== undefined && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: trendColor }}>
              <TrendIcon size={12} />
              {trend != null ? `${trend > 0 ? '+' : ''}${trend}% vs prior` : 'No prior data'}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 style={{
      fontSize: 13,
      fontWeight: 600,
      color: 'var(--text-muted)',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      margin: '28px 0 12px',
    }}>
      {children}
    </h3>
  )
}

function SkeletonBlock({ height = 200 }: { height?: number }) {
  return (
    <div style={{
      height,
      borderRadius: 10,
      background: 'linear-gradient(90deg, var(--bg-raised) 25%, var(--bg-surface) 50%, var(--bg-raised) 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.4s infinite',
    }} />
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function UsageDashboard() {
  const [presetIdx, setPresetIdx]     = useState(0)
  const [since, setSince]             = useState(PRESET_RANGES[0].getSince())
  const [until, setUntil]             = useState(PRESET_RANGES[0].getUntil())

  const [summary, setSummary]         = useState<Summary | null>(null)
  const [daily, setDaily]             = useState<DayEntry[]>([])
  const [users, setUsers]             = useState<UserRow[]>([])
  const [projects, setProjects]       = useState<ProjectRow[]>([])
  const [loading, setLoading]         = useState(true)
  const [exporting, setExporting]     = useState(false)

  const qs = `since=${encodeURIComponent(since)}&until=${encodeURIComponent(until)}`

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [s, d, u, p] = await Promise.all([
        apiFetch(`/api/usage/summary?${qs}`).then(r => r.json()),
        apiFetch(`/api/usage/daily?${qs}`).then(r => r.json()),
        apiFetch(`/api/usage/by-user?${qs}`).then(r => r.json()),
        apiFetch(`/api/usage/by-project?${qs}`).then(r => r.json()),
      ])
      setSummary(s)
      setDaily(d.days || [])
      setUsers(u.users || [])
      setProjects(p.projects || [])
    } catch (e) {
      console.error('Usage fetch failed', e)
    } finally {
      setLoading(false)
    }
  }, [qs])

  useEffect(() => { load() }, [load])

  function applyPreset(idx: number) {
    setPresetIdx(idx)
    setSince(PRESET_RANGES[idx].getSince())
    setUntil(PRESET_RANGES[idx].getUntil())
  }

  async function handleExport() {
    setExporting(true)
    try {
      const res = await apiFetch(`/api/usage/export?${qs}`)
      const blob = await res.blob()
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = since === allTimeStart()
        ? 'token_usage_all_time.csv'
        : `token_usage_${since.slice(0,10)}_to_${until.slice(0,10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setExporting(false)
    }
  }

  // Build donut data from daily call_type totals
  const donutData = (['agent', 'summarize', 'extract_api_profile'] as const)
    .map(ct => ({
      name:  CALL_TYPE_LABELS[ct],
      value: daily.reduce((acc, d) => acc + (d[ct]?.total_tokens ?? 0), 0),
      color: CALL_TYPE_COLORS[ct],
    }))
    .filter(d => d.value > 0)

  // Recharts area data
  const areaData = daily.map(d => ({
    date:               shortDate(d.date),
    agent:              d.agent?.total_tokens              ?? 0,
    summarize:          d.summarize?.total_tokens          ?? 0,
    extract_api_profile:d.extract_api_profile?.total_tokens?? 0,
  }))

  return (
    <div style={{ paddingBottom: 48 }}>
      {/* ── Shimmer keyframe ── */}
      <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>

      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {PRESET_RANGES.map((p, i) => (
          <button
            key={p.label}
            onClick={() => applyPreset(i)}
            style={{
              padding: '5px 12px',
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: presetIdx === i ? 'var(--accent)' : 'var(--bg-raised)',
              color: presetIdx === i ? '#000' : 'var(--text-primary)',
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={handleExport}
          disabled={exporting || loading}
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 14px',
            borderRadius: 6,
            border: '1px solid var(--border)',
            background: 'var(--bg-raised)',
            color: 'var(--text-primary)',
            fontSize: 12,
            fontWeight: 500,
            cursor: exporting ? 'not-allowed' : 'pointer',
            opacity: exporting ? 0.6 : 1,
          }}
        >
          <Download size={13} />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {/* ── Summary cards ── */}
      {loading ? (
        <div style={{ display: 'flex', gap: 12 }}>
          {[1,2,3,4].map(i => <SkeletonBlock key={i} height={90} />)}
        </div>
      ) : summary && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <StatCard
            label="Total tokens"
            value={fmtTokens(summary.total_tokens)}
            sub={`${fmtTokens(summary.input_tokens)} in · ${fmtTokens(summary.output_tokens)} out`}
            trend={summary.trend_pct}
          />
          <StatCard
            label="Estimated cost"
            value={fmtCost(summary.estimated_cost_usd)}
            sub={`${summary.call_count} API calls`}
          />
          <StatCard
            label="Active users"
            value={String(summary.active_users)}
          />
          <StatCard
            label="Active projects"
            value={String(summary.active_projects)}
          />
        </div>
      )}

      {/* ── Daily burn chart ── */}
      <SectionTitle>Daily Token Burn</SectionTitle>
      {loading ? <SkeletonBlock height={220} /> : daily.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No data for this period.</p>
      ) : (
        <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 8px 8px' }}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={areaData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <defs>
                {(['agent','summarize','extract_api_profile'] as const).map(ct => (
                  <linearGradient key={ct} id={`grad-${ct}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={CALL_TYPE_COLORS[ct]} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={CALL_TYPE_COLORS[ct]} stopOpacity={0}    />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
              <YAxis tickFormatter={v => fmtTokens(v)} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} width={48} />
              <ReTooltip
                contentStyle={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                formatter={(v: number, name: string) => [fmtTokens(v), CALL_TYPE_LABELS[name] ?? name]}
              />
              <Legend formatter={(v: string) => CALL_TYPE_LABELS[v] ?? v} wrapperStyle={{ fontSize: 12 }} />
              {(['agent','summarize','extract_api_profile'] as const).map(ct => (
                <Area
                  key={ct}
                  type="monotone"
                  dataKey={ct}
                  stackId="1"
                  stroke={CALL_TYPE_COLORS[ct]}
                  fill={`url(#grad-${ct})`}
                  strokeWidth={2}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Call type donut ── */}
      {!loading && donutData.length > 0 && (
        <>
          <SectionTitle>Token Share by Call Type</SectionTitle>
          <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, display: 'flex', justifyContent: 'center' }}>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={donutData} dataKey="value" cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3}>
                  {donutData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <ReTooltip
                  contentStyle={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                  formatter={(v: number, name: string) => [fmtTokens(v), name]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/* ── Users table ── */}
      <SectionTitle>By User</SectionTitle>
      {loading ? <SkeletonBlock height={120} /> : users.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No data for this period.</p>
      ) : (
        <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
                {['User', 'Tokens', 'Cost', 'Conversations', 'API Calls', 'Last Active'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.user_id} style={{ borderBottom: i < users.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <td style={{ padding: '10px 14px', color: 'var(--text-primary)', fontWeight: 500 }}>{u.email}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-primary)' }}>{fmtTokens(u.total_tokens)}</td>
                  <td style={{ padding: '10px 14px', color: '#10b981', fontWeight: 600 }}>{fmtCost(u.estimated_cost_usd)}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{u.conversation_count}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{u.call_count}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{fmtDate(u.last_active)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Projects table ── */}
      <SectionTitle>By Project</SectionTitle>
      {loading ? <SkeletonBlock height={120} /> : projects.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No data for this period.</p>
      ) : (
        <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
                {['Project', 'Tokens', 'Cost', 'Conversations', 'API Calls', 'Last Active'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projects.map((p, i) => (
                <tr key={p.project_id} style={{ borderBottom: i < projects.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <td style={{ padding: '10px 14px', color: 'var(--text-primary)', fontWeight: 500 }}>{p.project_name}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-primary)' }}>{fmtTokens(p.total_tokens)}</td>
                  <td style={{ padding: '10px 14px', color: '#10b981', fontWeight: 600 }}>{fmtCost(p.estimated_cost_usd)}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{p.conversation_count}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{p.call_count}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{fmtDate(p.last_active)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
